import { useState, useEffect, useCallback, useRef } from 'react'
import { HttpClient, HttpError } from '@src/service'
import { MattermostSessionResponse } from '../types'
import { getMattermostSession, checkTokenHealth } from '../services/mattermost-api'

const TOKEN_HEALTH_INTERVAL_MS = 5 * 60 * 1000

interface UseMattermostSessionResult {
  session: MattermostSessionResponse | null
  loading: boolean
  error: string | null
  refresh: () => void
}

export function useMattermostSession(
  httpClient: HttpClient,
  organizationId: string | null | undefined
): UseMattermostSessionResult {
  const [session, setSession] = useState<MattermostSessionResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchSession = useCallback(async () => {
    if (!organizationId) {
      setLoading(false)
      setError('Organization not found')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const result = await getMattermostSession(httpClient, organizationId)
      if (result instanceof HttpError) {
        setError(result.message || `Request failed (${result.status})`)
      } else if (result && (result as any).error) {
        setError((result as any).error)
      } else {
        setSession(result)
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to create messaging session')
    } finally {
      setLoading(false)
    }
  }, [httpClient, organizationId])

  useEffect(() => {
    fetchSession()
  }, [fetchSession])

  // Periodic token health check — refresh session if PAT becomes invalid
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  useEffect(() => {
    if (!session || !organizationId) return
    intervalRef.current = setInterval(async () => {
      try {
        const health = await checkTokenHealth(httpClient)
        if (health && !(health instanceof HttpError) && !health.valid) {
          fetchSession()
        }
      } catch { /* health check is best-effort */ }
    }, TOKEN_HEALTH_INTERVAL_MS)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [session, organizationId, httpClient, fetchSession])

  return { session, loading, error, refresh: fetchSession }
}
